import { Box, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import Banner from '@components/ContractComponents/Banner';
import Breadcrumb from '@components/breadcrumb/Breadcrumb';
import { ConsultaCredito } from '@/types/credito.ts';
import { ContractContainer } from './style';
import ContractForm from '@components/ContractComponents/Form';
import { ContratoFormData } from '@/schemas/contratoSchema.ts';
import { Header } from '@components/Header';
import SucessContract from '@components/ContractComponents/SucessContract';
import ToastTimer from '@components/ContractComponents/ToastTimer';
import PDFViewer from '@components/PdfViewer';
import { Check } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';

import {
  EmitirApoliceResponse,
  GerarMinutaApiResponse,
  GerarMinutaResponse,
  OrcamentoResponse,
} from '@libs/types';
import { fetchAsasToken } from '@libs/asasAuth';
import LimitExceeded from '@components/ContractComponents/LimitExceeded';
import { logger } from '@/tracer';
import { SeverityNumber } from '@opentelemetry/api-logs';
import { ORIGIN_ID } from '@utils/flowId';

// Tempo de espera excedido, por favor entre em contato com o suporte@exemplo.com.

function formatCurrency(value: number) {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

function ContractPage() {
  // step 1: formulário | step 2: seleção de parcelas | step 3: sucesso | step 4: limite excedido
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [dados, setDados] = useState<ConsultaCredito | null>(null);
  const [mounted, setMounted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [orcamento, setOrcamento] = useState<OrcamentoResponse | null>(null);
  const [selectedParcela, setSelectedParcela] = useState<number | null>(null);
  const [termoAceite, setTermoAceite] = useState(false);
  const [termoMinuta, setTermoMinuta] = useState(false);
  const [savedFormData, setSavedFormData] = useState<ContratoFormData | null>(
    null,
  );
  const [idEndosso, setIdEndosso] = useState<number | null>(null);
  const [valorIsContratado, setValorIsContratado] = useState<number | null>(
    null,
  );
  const [apoliceData, setApoliceData] = useState<EmitirApoliceResponse | null>(
    null,
  );
  const [timeoutProgress, setTimeoutProgress] = useState(0);
  const [flowId, setFlowId] = useState<string>('');

  const navigate = useNavigate();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const dParam = searchParams.get('d');

    if (dParam) {
      try {
        const decoded = JSON.parse(atob(decodeURIComponent(dParam)));
        localStorage.setItem('consultaCredito', JSON.stringify(decoded));
        window.history.replaceState({}, '', window.location.pathname);
        setDados(decoded);
        return;
      } catch {
        // param malformado, cai para verificação do storage
      }
    }

    const storage = localStorage.getItem('consultaCredito');
    if (storage) {
      try {
        setDados(JSON.parse(storage));
      } catch {
        navigate({ to: '/' });
      }
      return;
    }

    navigate({ to: '/' });
  }, [navigate]);

  useEffect(() => {
    const id = setTimeout(() => setMounted(true), 20);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (step === 2 || step === 3) {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        console.error('Error scrolling to top:', e);
      }
    }
  }, [step]);

  function startTimeoutProgress(duration = 180000, onFinish?: () => void) {
    const start = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const progress = Math.min((elapsed / duration) * 100, 100);

      setTimeoutProgress(progress);

      if (progress >= 100) {
        clearInterval(interval);
        onFinish?.();
      }
    }, 100);

    return () => clearInterval(interval);
  }


  const onSubmit = async (data: ContratoFormData) => {
    if (!dados) return;

      const cadastralAttrs = {
        'user.nome': dados.name,
        'user.email': dados.email,
        'user.telefone': dados.telefone,
        'user.cnpj': dados.cnpj,
        'corretor.id': dados.idCorretor,
      };

      logger.emit({
        severityNumber: SeverityNumber.INFO,
        severityText: 'INFO',
        body: 'api.quote.requested',
        attributes: {
          ...cadastralAttrs,
          'flow.id': flowId,
          'origin.id': ORIGIN_ID,
          'tomador.id': dados.idTomador,
          'modalidade.id': dados.idModalidade,
          'contrato.valor_is': data.valorIS,
          'user.razaosocial': data.nomeSegurado
        },
      });

      setErrorMessage(null);
      setIsSubmitting(true);
      setTimeoutProgress(0);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000);
      const stopProgress = startTimeoutProgress(180000, () => {
        setErrorMessage(
          'Tempo de espera excedido, por favor entre em contato com o suporte.',
        );
        controller.abort();
      });

      try {
        const token = await fetchAsasToken();

        const payload = {
          cep: data.cep,
          complemento: data.complemento,
          cpfCnpjSegurado: data.cpfCnpjSegurado,
          dtInicioVigencia: data.dtInicioVigencia,
          dtFimVigencia: data.dtFimVigencia,
          idSeguradora: 13,
          idModalidade: 17,
          idRamo: 775,
          idTomador: dados.idTomador,
          idCorretor: dados.idCorretor,
          logradouro: data.logradouro,
          municipio: data.municipio,
          telefone: dados.telefone,
          email: dados.email,
          nomeSegurado: data.nomeSegurado,
          numero: data.numero,
          numeroEdital: data.numeroEdital,
          termoAceite: true,
          tipoPessoa: 'PJ',
          bairro: data.bairro,
          uf: data.uf,
          valorIS: data.valorIS,
          base64Edital: data.base64Edital,
        };

        const response = await fetch(
          `${process.env.API_ASAS_GARANTIA_URL}/services/garantia-api/licitacoes/gerarMinuta`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
            signal: controller.signal,
          },
        );

        const resData = (await response.json()) as GerarMinutaApiResponse;

        if (!response.ok || resData.numeroMinuta === null) {
          const businessMsg = (resData as any)?.message || `Erro ao gerar cotação (${response.status}).`;
          logger.emit({
            severityNumber: SeverityNumber.WARN,
            severityText: 'WARN',
            body: 'api.quote.business_message',
            attributes: {
              ...cadastralAttrs,
              'flow.id': flowId,
              'origin.id': ORIGIN_ID,
              'tomador.id': dados.idTomador,
              'business.message': businessMsg,
            },
          });
          setErrorMessage(businessMsg);
          return;
        }

        const condicao = resData.condicoes?.[0];
        if (!condicao || !condicao.formaPagamentoParcela?.length) {
          logger.emit({
            severityNumber: SeverityNumber.WARN,
            severityText: 'WARN',
            body: 'api.quote.business_message',
            attributes: {
              ...cadastralAttrs,
              'flow.id': flowId,
              'origin.id': ORIGIN_ID,
              'tomador.id': dados.idTomador,
              'business.message': 'Não foi possível obter as condições de pagamento.',
            },
          });
          setErrorMessage('Não foi possível obter as condições de pagamento.');
          return;
        }

        setIdEndosso(resData.idEndosso);
        setValorIsContratado(data.valorIS);
        setOrcamento({
          base64Minuta: resData.base64Minuta,
          valorSegurado: data.valorIS,
          premio: condicao.premio,
          vencimentoPrimeiraParcela: condicao.vencimentoPrimeiraParcela,
          formaPagamento: condicao.formaPagamento,
          parcelas: condicao.formaPagamentoParcela,
        });
        setSavedFormData(data);
        setSelectedParcela(null);
        logger.emit({
          severityNumber: SeverityNumber.INFO,
          severityText: 'INFO',
          body: 'api.quote.success',
          attributes: {
            ...cadastralAttrs,
            'flow.id': flowId,
            'origin.id': ORIGIN_ID,
            'tomador.id': dados.idTomador,
            'contrato.id_endosso': resData.idEndosso,
          },
        });
        setStep(2);
      } catch (error: any) {
        logger.emit({
          severityNumber: SeverityNumber.ERROR,
          severityText: 'ERROR',
          body: 'api.quote.failed',
          attributes: {
            ...cadastralAttrs,
            'flow.id': flowId,
            'origin.id': ORIGIN_ID,
            message: error?.message,
            name: error?.name,
          },
        });
        if (error.name === 'AbortError') {
          setErrorMessage(
            'Tempo de espera excedido, por favor entre em contato com o suporte.',
          );
        } else {
          setErrorMessage(
            'Ocorreu um erro ao processar sua solicitação. Tente novamente.',
          );
        }
      } finally {
        clearTimeout(timeoutId);
        stopProgress();
        setIsSubmitting(false);
        setTimeoutProgress(0);
      }
  };



  const handleContrate = async () => {
    if (selectedParcela === null || !orcamento || !dados) return;

    const parcela = orcamento.parcelas[selectedParcela];

    const cadastralAttrs = {
      'user.nome': savedFormData?.nomeSegurado ?? '',
      'user.email': dados.email,
      'user.telefone': dados.telefone,
      'user.cnpj': dados.cnpj,
      'corretor.id': dados.idCorretor,
    };

    logger.emit({
      severityNumber: SeverityNumber.INFO,
      severityText: 'INFO',
      body: 'api.contract.requested',
      attributes: {
        ...cadastralAttrs,
        'flow.id': flowId,
        'origin.id': ORIGIN_ID,
        'contrato.id_endosso': idEndosso,
        'tomador.id': dados.idTomador,
        'parcela.id': parcela.idParcela,
        'parcela.valor': parcela.valorParcela,
        'contrato.valor_is': valorIsContratado,
      },
    });

    setIsSubmitting(true);
    setErrorMessage(null);
    setTimeoutProgress(0);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 180000);
    const stopProgress = startTimeoutProgress(180000, () => {
      setErrorMessage(
        'Tempo de espera excedido, por favor entre em contato com o suporte.',
      );
      controller.abort();
    });

    try {
      const token = await fetchAsasToken();

      const emitirPayload = {
        idCorretor: dados.idCorretor,
        idTomador: dados.idTomador,
        idEndosso,
        termoAceite: true,
        diasVencimentoPrimeiraParcela: 15,
        formaPagamento: orcamento.formaPagamento,
        formaPagamentoParcela: parcela.idParcela,
      };

      const response = await fetch(
        `${process.env.API_ASAS_GARANTIA_URL}/services/garantia-api/licitacoes/emitirApolice`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(emitirPayload),
          signal: controller.signal,
        },
      );

      const resData = (await response.json()) as EmitirApoliceResponse;

      if (!response.ok) {
        logger.emit({
          severityNumber: SeverityNumber.ERROR,
          severityText: 'ERROR',
          body: 'api.contract.failed',
          attributes: {
            ...cadastralAttrs,
            'flow.id': flowId,
            'origin.id': ORIGIN_ID,
            'contrato.id_endosso': idEndosso,
            'http.status': response.status,
            message: resData.message ?? null,
          },
        });
        setErrorMessage(
          resData.message || `Erro ao emitir apólice (${response.status}).`,
        );
        return;
      }

      if (resData.message) {
        logger.emit({
          severityNumber: SeverityNumber.WARN,
          severityText: 'WARN',
          body: 'api.contract.business_message',
          attributes: {
            ...cadastralAttrs,
            'flow.id': flowId,
            'origin.id': ORIGIN_ID,
            'contrato.id_endosso': idEndosso,
            'business.message': resData.message,
          },
        });
        setErrorMessage(resData.message);
        return;
      }

      logger.emit({
        severityNumber: SeverityNumber.INFO,
        severityText: 'INFO',
        body: 'api.contract.success',
        attributes: {
          ...cadastralAttrs,
          'flow.id': flowId,
          'origin.id': ORIGIN_ID,
          'contrato.id_endosso': idEndosso,
          'tomador.id': resData.idTomador,
          'contrato.numero_apolice': resData.numeroApolice,
        },
      });


      setApoliceData(resData);
      setStep(3);
    } catch (err: any) {
      logger.emit({
        severityNumber: SeverityNumber.ERROR,
        severityText: 'ERROR',
        body: 'api.contract.failed',
        attributes: {
          ...cadastralAttrs,
          'flow.id': flowId,
          'origin.id': ORIGIN_ID,
          message: err?.message,
          name: err?.name,
        },
      });
      if (err.name === 'AbortError') {
        setErrorMessage('Tempo de espera excedido. Tente novamente.');
      } else {
        setErrorMessage('Ocorreu um erro ao processar. Tente novamente.');
      }
    } finally {
      clearTimeout(timeoutId);
      stopProgress();
      setIsSubmitting(false);
      setTimeoutProgress(0);
    }
  };

  const successData: GerarMinutaResponse | null = apoliceData
    ? {
      idTomador: apoliceData.idTomador,
      idContrato: null,
      numeroMinuta: apoliceData.numeroApolice,
      base64Apolice: apoliceData.base64Apolice,
      base64Boleto: apoliceData.base64Boleto,
      businessMessage: null,
      message: null,
      valorIs: valorIsContratado,
      vencimentoBoleto: orcamento?.vencimentoPrimeiraParcela ?? null,
      valorBoleto:
        selectedParcela !== null && orcamento
          ? (orcamento.parcelas[selectedParcela]?.valorParcela?.toFixed(2) ??
            null)
          : null,
    }
    : null;

  return (
    <ContractContainer>
      <Header />

      <main>
        <Breadcrumb />
        <Box
          component="section"
          sx={{
            transition: 'transform 300ms ease, opacity 300ms ease',
            transform: mounted ? 'translateX(0)' : 'translateX(-20px)',
            opacity: mounted ? 1 : 0,
          }}
        >
          <Banner />

          {/* Step 1: Formulário */}
          {step === 1 && dados && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { md: 'center' },
                  flexDirection: { xs: 'column', md: 'row' },
                  paddingX: { xs: '24px', md: '88px' },
                  paddingY: { xs: '40px', md: '70px' },
                  gap: '16px',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '18px', md: '24px' },
                    color: '#004AA0',
                  }}
                >
                  Contratação de Seguro Garantia
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '16px' },
                    maxWidth: { md: '460px' },
                    color: '#808080',
                  }}
                >
                  Preencha os dados abaixo para finalizar seu seguro garantia.
                  Todos os campos marcados com asterisco (*) são obrigatórios.
                </Typography>
              </Box>
              <ContractForm
                limiteCredito={dados.limiteCredito}
                onSubmit={onSubmit}
                setErrorMessage={setErrorMessage}
                isSubmitting={isSubmitting}
                timeoutProgress={timeoutProgress}
                defaultValues={savedFormData ?? undefined}
                errorMessage={errorMessage}
              />
            </>
          )}

          {/* Step 2: Seleção de parcelas */}
          {step === 2 && orcamento && (
            <Box sx={{ pt: { xs: '24px', md: '40px' } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { md: 'center' },
                  flexDirection: { xs: 'column', md: 'row' },
                  paddingX: { xs: '24px', md: '88px' },
                  pb: { xs: '24px', md: '40px' },
                  gap: '16px',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '18px', md: '24px' },
                    color: '#004AA0',
                  }}
                >
                  Orçamento do Seguro Garantia
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '16px' },
                    maxWidth: { md: '460px' },
                    color: '#808080',
                  }}
                >
                  Escolha a forma de pagamento e finalize sua contratação.
                </Typography>
              </Box>

              {/* Resumo do prêmio */}
              <Box
                sx={{
                  mx: { xs: '24px', md: '88px' },
                  mb: { xs: '24px', md: '32px' },
                  p: { xs: '20px', md: '32px' },
                  borderRadius: '12px',
                  backgroundColor: '#FFF',
                  boxShadow: '0px 4px 4px 0px #00000040',
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: '12px', md: '48px' },
                  alignItems: { md: 'center' },
                }}
              >
                <Box>
                  <Typography
                    sx={{ fontSize: '12px', color: '#808080', mb: '4px' }}
                  >
                    Valor Segurado
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '20px', md: '28px' },
                      fontWeight: 700,
                      color: '#004AA0',
                    }}
                  >
                    {formatCurrency(orcamento.valorSegurado)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: { md: '1px' },
                    height: { xs: '1px', md: '48px' },
                    backgroundColor: '#E0E0E0',
                  }}
                />
                <Box>
                  <Typography
                    sx={{ fontSize: '12px', color: '#808080', mb: '4px' }}
                  >
                    Prêmio Total
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '20px', md: '28px' },
                      fontWeight: 700,
                      color: '#004AA0',
                    }}
                  >
                    {formatCurrency(orcamento.premio)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    ml: { md: 'auto' },
                    p: { xs: '8px 12px', md: '10px 20px' },
                    borderRadius: '8px',
                    backgroundColor: '#E8F0FD',
                  }}
                >
                  <Typography
                    sx={{ fontSize: '12px', color: '#004AA0', fontWeight: 600 }}
                  >
                    Pagamento: Boleto Bancário
                  </Typography>
                </Box>
              </Box>

              {/* Cards de parcela */}
              <Box
                sx={{
                  px: { xs: '24px', md: '88px' },
                  mb: { xs: '24px', md: '32px' },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '16px', md: '18px' },
                    color: '#004AA0',
                    mb: '16px',
                  }}
                >
                  Escolha a forma de pagamento
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: `repeat(${Math.min(orcamento.parcelas.length, 4)}, 1fr)`,
                    },
                    gap: '16px',
                  }}
                >
                  {orcamento.parcelas.map((parcela, idx) => {
                    const isSelected = selectedParcela === idx;
                    return (
                      <Box
                        key={parcela.idParcela}
                        onClick={() => setSelectedParcela(idx)}
                        sx={{
                          cursor: 'pointer',
                          borderRadius: '12px',
                          border: `2px solid ${isSelected ? '#004AA0' : '#E0E0E0'}`,
                          backgroundColor: isSelected ? '#E8F0FD' : '#FFF',
                          boxShadow: isSelected
                            ? '0px 4px 12px 0px #004AA020'
                            : '0px 2px 4px 0px #00000015',
                          p: { xs: '16px', md: '24px' },
                          transition: 'all 0.15s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                          position: 'relative',
                        }}
                      >
                        {/* Radio indicator */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            border: `2px solid ${isSelected ? '#004AA0' : '#BDBDBD'}`,
                            backgroundColor: isSelected
                              ? '#004AA0'
                              : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {isSelected && (
                            <Box
                              sx={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#FFF',
                              }}
                            />
                          )}
                        </Box>

                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '22px', md: '28px' },
                            color: isSelected ? '#004AA0' : '#333',
                          }}
                        >
                          {parcela.idParcela === 1
                            ? 'À vista'
                            : `${parcela.idParcela}x`}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: '16px', md: '20px' },
                            color: isSelected ? '#004AA0' : '#555',
                          }}
                        >
                          {formatCurrency(parcela.valorParcela)}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#808080' }}>
                          {parcela.jurosParcela}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box
                sx={{
                  px: { xs: '24px', md: '88px' },
                  mb: { xs: '24px', md: '32px' },
                }}
              >
                {orcamento.base64Minuta ? (
                  <Box
                    sx={{
                      mb: '16px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #E0E0E0',
                      backgroundColor: '#FFF',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <PDFViewer base64={orcamento.base64Minuta} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      mb: '16px',
                      p: '16px',
                      borderRadius: '8px',
                      backgroundColor: '#FF950026',
                      border: '1px solid #FF9500',
                    }}
                  >
                    <Typography sx={{ color: '#808080', fontSize: '14px' }}>
                      Não foi possível carregar a minuta no momento.
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Termos e Condições */}
              <Box
                sx={{
                  px: { xs: '24px', md: '88px' },
                  mb: { xs: '24px', md: '32px' },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '16px', md: '18px' },
                    color: '#004AA0',
                    mb: '16px',
                  }}
                >
                  Termos e Condições
                </Typography>
                <Box
                  component={'div'}
                  sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
                >
                  <Box
                    component="label"
                    sx={{
                      backgroundColor: termoMinuta ? '#00B90626' : '#FF950026',
                      padding: '24px',
                      paddingRight: { md: '40px' },
                      border: `1px solid ${termoMinuta ? '#00B906' : '#FF9500'}`,
                      borderRadius: '4px',
                      display: 'flex',
                      gap: '24px',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.1s',
                    }}
                  >
                    <Box
                      sx={{
                        width: '20px',
                        height: '20px',
                        minWidth: '20px',
                        position: 'relative',
                        boxShadow: '0px 4px 4px 0px #00000040 inset',
                        backgroundColor: '#FFF',
                        padding: '2px',
                      }}
                    >
                      <Typography
                        sx={{
                          opacity: termoMinuta ? 1 : 0,
                          transition: 'opacity 0.1s ease-in-out',
                        }}
                      >
                        <Check color="#00B906" size={16} weight="bold" />
                      </Typography>
                    </Box>
                    <Box sx={{ color: '#808080' }}>
                      <Typography
                        sx={{ fontSize: '16px', fontWeight: 700, mb: '20px' }}
                      >
                        Li e aceito os termos da Minuta da Apólice de Seguro Garantia *
                      </Typography>
                      <Typography sx={{ fontSize: '16px' }}>
                        Declaro que li a minuta da Apólice de Seguro Garantia,
                        emitida pela Allseg Seguradora, compreendi as
                        coberturas, exclusões, vigência e condições gerais, e
                        estou de acordo com seus termos.
                      </Typography>
                    </Box>
                    <input
                      type="checkbox"
                      checked={termoMinuta}
                      onChange={(e) => setTermoMinuta(e.target.checked)}
                      disabled={isSubmitting}
                      hidden
                    />
                  </Box>

                  <Box
                    component="label"
                    sx={{
                      backgroundColor: termoAceite ? '#00B90626' : '#FF950026',
                      padding: '24px',
                      paddingRight: { md: '40px' },
                      border: `1px solid ${termoAceite ? '#00B906' : '#FF9500'}`,
                      borderRadius: '4px',
                      display: 'flex',
                      gap: '24px',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.1s',
                    }}
                  >
                    <Box
                      sx={{
                        width: '20px',
                        height: '20px',
                        minWidth: '20px',
                        position: 'relative',
                        boxShadow: '0px 4px 4px 0px #00000040 inset',
                        backgroundColor: '#FFF',
                        padding: '2px',
                      }}
                    >
                      <Typography
                        sx={{
                          opacity: termoAceite ? 1 : 0,
                          transition: 'opacity 0.1s ease-in-out',
                        }}
                      >
                        <Check color="#00B906" size={16} weight="bold" />
                      </Typography>
                    </Box>
                    <Box sx={{ color: '#808080' }}>
                      <Typography
                        sx={{ fontSize: '16px', fontWeight: 700, mb: '20px' }}
                      >
                        Li e aceito os termos da Carta de Nomeação *
                      </Typography>
                      <Typography sx={{ fontSize: '16px' }}>
                        Para esse limite de crédito, é necessário o aceite
                        formal por Carta de Nomeação especifica para contratação
                        de seguro nesse ambiente. Ao marcar esta opção, você
                        declara estar ciente e de acordo com esse procedimento.
                      </Typography>
                    </Box>
                    <input
                      type="checkbox"
                      checked={termoAceite}
                      onChange={(e) => setTermoAceite(e.target.checked)}
                      disabled={isSubmitting}
                      hidden
                    />
                  </Box>
                </Box>
              </Box>

              {/* Ações */}
              <Box
                sx={{
                  px: { xs: '24px', md: '88px' },
                  pb: { xs: '40px', md: '56px' },
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: '24px',
                  alignItems: 'center',
                }}
              >
                <Box
                  component="button"
                  onClick={() => {
                    setStep(1);
                    setOrcamento(null);
                    setSelectedParcela(null);
                    setTermoAceite(false);
                    setErrorMessage(null);
                  }}
                  disabled={isSubmitting}
                  sx={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#808080',
                    textDecoration: 'underline',
                    fontWeight: 700,
                    fontSize: '14px',
                    fontFamily: 'Montserrat, sans-serif',
                    '&:disabled': { opacity: 0.5 },
                  }}
                >
                  Voltar ao Formulário
                </Box>

                <Button
                  variant="contained"
                  onClick={handleContrate}
                  disabled={
                    selectedParcela === null || !termoAceite || isSubmitting
                  }
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: '#004AA0',
                    fontWeight: 700,
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    padding: '8px 44px',
                    textTransform: 'none',
                    width: { xs: '100%', md: '325px' },
                    '&:hover': { backgroundColor: '#003c85' },
                    '&:disabled': { backgroundColor: '#ccc', color: '#FFF' },
                  }}
                >
                  <Box
                    component="span"
                    sx={{ position: 'relative', zIndex: 1 }}
                  >
                    {isSubmitting ? 'Processando...' : 'Contrate Agora'}
                  </Box>
                  {!!isSubmitting && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: `${timeoutProgress}%`,
                        backgroundColor: '#2064b8',
                        zIndex: 0,
                        transition: 'width 100ms linear',
                      }}
                    />
                  )}
                </Button>
              </Box>

              {errorMessage && (
                <Box
                  sx={{
                    mx: { xs: '24px', md: '88px' },
                    mb: '24px',
                    p: '16px 24px',
                    borderRadius: '8px',
                    backgroundColor: '#FF950026',
                    border: '1px solid #FF9500',
                  }}
                >
                  <Typography sx={{ color: '#808080', fontSize: '14px' }}>
                    {errorMessage}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Step 3: Sucesso */}
          {step === 3 && successData && <SucessContract dados={successData} />}

          {/* Step 4: Limite excedido */}
          {step === 4 && (
            <LimitExceeded
              dados={{
                idTomador: null,
                idContrato: null,
                numeroMinuta: null,
                base64Apolice: null,
                base64Boleto: null,
                businessMessage: null,
                message: null,
                valorIs: null,
              }}
            />
          )}
        </Box>
      </main>
      {step !== 3 && <ToastTimer />}
    </ContractContainer>
  );
}

export default ContractPage;
